/**
 * VoiceInputButton — Whisper-Flow-style mic toggle for the chat composer.
 *
 * Presentational component: the parent (ChatInput) owns `useVoiceInput`
 * and passes state + start/stop/levels/duration as props. This split
 * lets the textarea call `stop()` when the user starts typing during
 * a recording, and lets transcripts flow into the textarea at the
 * caret position rather than always at the end.
 *
 * Two visual states:
 *   Idle  → 32×32 (30×30 mobile) circle that mirrors the attach button.
 *           Single mic icon. One tap starts listening.
 *   Live  → expanded pill with a pulsing red dot, five reactive bars
 *           whose heights track real microphone levels (AnalyserNode),
 *           and a MM:SS timer. One tap (or 60-second auto-stop) ends it.
 */

import { useEffect, useRef } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { VOICE_BAR_COUNT, type VoiceInputState } from '../hooks/useVoiceInput';

interface VoiceInputButtonProps {
  state: VoiceInputState;
  levelsRef: React.MutableRefObject<number[]>;
  durationSeconds: number;
  start: () => void;
  stop: () => void;
  disabled?: boolean;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function VoiceInputButton({
  state,
  levelsRef,
  durationSeconds,
  start,
  stop,
  disabled = false,
}: VoiceInputButtonProps) {
  const { t } = useLanguage();
  const isListening = state === 'listening';

  // Drive per-bar heights from `levelsRef` directly via DOM CSS
  // variables. Doing this in our own rAF loop (instead of setState)
  // means the bars react at 60fps without re-rendering the React tree.
  const barsRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!isListening) return;
    let raf = 0;
    const apply = () => {
      const root = barsRef.current;
      if (root) {
        const levels = levelsRef.current;
        for (let i = 0; i < levels.length; i += 1) {
          // Clamp to a visible floor so silence still shows a sliver of
          // bar — communicates "listening" while the room is quiet.
          const v = Math.max(0.12, Math.min(1, levels[i]));
          root.style.setProperty(`--voice-level-${i}`, v.toString());
        }
      }
      raf = requestAnimationFrame(apply);
    };
    raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [isListening, levelsRef]);

  return (
    <button
      type="button"
      className={`voice-input-btn${isListening ? ' listening' : ''}`}
      onClick={() => (isListening ? stop() : start())}
      disabled={disabled}
      title={
        isListening
          ? t('chat.voice.stop', { defaultValue: 'Stop dictation' })
          : t('chat.voice.start', { defaultValue: 'Voice input' })
      }
      aria-label={
        isListening
          ? t('chat.voice.stop', { defaultValue: 'Stop dictation' })
          : t('chat.voice.start', { defaultValue: 'Voice input' })
      }
      data-testid="voice-input-button"
    >
      {isListening ? (
        <span className="voice-pill" aria-hidden="true">
          <span className="voice-rec-dot" />
          <span className="voice-wave" ref={barsRef}>
            {Array.from({ length: VOICE_BAR_COUNT }).map((_, i) => (
              <span key={i} className="voice-wave-bar" />
            ))}
          </span>
          <span className="voice-timer">{formatDuration(durationSeconds)}</span>
        </span>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="2" width="6" height="12" rx="3" />
          <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="8" y1="22" x2="16" y2="22" />
        </svg>
      )}
    </button>
  );
}
